'use strict';


import { EnvironmentLayer } from './EnvironmentLayer';
import { OrbitDayAndNight } from '../../../liveAnimations/DayNight/OrbitDayAndNight';
import { DayNightEffect } from '../../../post-processing/Passes/DayNightPass';
import { isMobile, projectMode, threeJsVariables } from '../../../../settings';
import { Loader } from '../../../threejs/loader/Loader';
import { Color, LinearSRGBColorSpace, Vector3 } from 'three';
import { LUT } from '../../../post-processing/Passes/LUT';

export class DayNightEnvironment extends EnvironmentLayer{
	
	constructor () {
		super('DayNight');
		
		this.radius = 3000;
		this.colors = {
			originAmbient: new Color(),
			targetAmbient: new Color()
		};
		this.sky = null;
		this.pass = new DayNightEffect(threeJsVariables.perspectiveCamera);
		threeJsVariables.composer.composer.insertPass(this.pass.effect,3);
	}
	
	create () {
		if ((isMobile && projectMode.AirMode.active) || (projectMode.AirMode.active && !projectMode.AirMode.hasSkyMask()))
			return;
		const sunGroup = OrbitDayAndNight.addSun({
			turbidity: 1.4158,
			rayleigh: 1.5733,
			mieCoefficient: 0.06,
			mieDirectionalG:  0.9999,
			useTime: true
		});
		this.group.add(sunGroup);
		this.sky = sunGroup.children[0];
		this.sky.scale.setScalar(this.radius);
		this.pass.setSky(this.sky);
		OrbitDayAndNight.sky = this.sky;
		OrbitDayAndNight.usePresets = false;
		threeJsVariables.composer.addUpdatableElement(this.pass.effect);
	}
	
	show () {
		this.group.visible = true;
		this.pass.active = !projectMode.AirMode.active;
	}
	
	hide () {
		this.group.visible = false;
		this.pass.active = false;
	}
	
	setAlpha(alpha) {
		if (!this.colors.originAmbient.equals(this.colors.targetAmbient)) {
			const sceneAmbient = threeJsVariables.scene.getObjectByName('ambientLight');
			sceneAmbient.color.lerpColors(this.colors.targetAmbient,this.colors.originAmbient,alpha);
		}
		
		projectMode.AirMode.updateAlpha(alpha);
		const luts = Object.values(LUT.scopes);
		for (let i = 0; i < luts.length; i++) {
			luts[i].updateTime(alpha);
		}
	}
	
	setDayTime(times) {
		this.pass.setDayTime(times);
		if (OrbitDayAndNight.sky?.shaderVersion === '1.0') {
			this.sky.material.uniforms.sunriseTime.value = times.sunrise;
			this.sky.material.uniforms.sunsetTime.value = times.sunset;
			this.sky.material.uniforms.dayStartTime.value = times.dayStart;
			this.sky.material.uniforms.dayEndTime.value = times.dayEnd;
		}
	}
	
	setTime(time) {
		this.pass.setTime(time);
		if (this.sky && OrbitDayAndNight.sky?.shaderVersion === '1.0')
			this.sky.material.uniforms.currentTime.value = time;
	}
	
	loadFromBundle (scene, conf) {
		const config = conf.dayNight;
		if (!config) return;
		this.settings = config;
		this.create();
	}
	
	set settings (data) {
		if (!data) return;
		if (Object.keys(data.luts).length) {
			const loader = new Loader().luteLoader;
			Object.entries(data.luts).forEach(([lutTime,lutData]) => {
				if (lutData.source) {
					const parsedLut = loader.parse(lutData.source);
					const texture = parsedLut.texture3D;
					texture.colorSpace = LinearSRGBColorSpace;
					this.pass.effect.uniforms[lutTime].value = texture;
					this.pass.effect.uniforms[lutTime + 'Size'].value = parsedLut.size;
					this.pass.effect.uniforms[lutTime + 'Intensity'].value = lutData.intensity;
				}
			});
		}
		
		const sceneAmbient = threeJsVariables.scene.getObjectByName('ambientLight');
		this.colors.originAmbient.copy(sceneAmbient.color);
		if (data.nightAmbient) {
			this.colors.targetAmbient.setStyle(data.nightAmbient,LinearSRGBColorSpace);
		} else {
			this.colors.targetAmbient.copy(sceneAmbient.color);
		}
		
		this.radius = data.radius;
		const nightLights = [];
		
		if (!projectMode.AirMode.active) {
			data.pointLights.forEach(data => {
				const light = new NightLight(data);
				nightLights.push(light.struct);
			});
		}
		
		if (nightLights.length > 0) {
			this.pass.effect.setLights(nightLights);
		}
		
		Object.entries(data.fog).forEach(([name,value]) => {
			this.pass.effect.uniforms[name].value = value;
		});
	}
	
}


class NightLight {
	constructor (data) {
		this.color = new Color().setStyle(data.color,LinearSRGBColorSpace);
		this.intensity = data.intensity;
		this.position = new Vector3().fromArray(data.position);
		this.rotation = new Vector3().fromArray(data.rotation);
		this.radius = data.radius;
	}
	
	get struct() {
		return {
			position: this.position,
			rotation: this.rotation,
			intensity: this.intensity,
			color: this.color,
			radius: this.radius
		}
	}
}
